/**
 * 工作流常量定义
 * 统一术语和配置
 */

import type { WorkflowStage, TrackType, TrackStatus } from '@company/shared'

// ==================== 操作标签 ====================

export const ACTION_LABELS = {
  submit_review: '提交审核',
  approve: '审核通过',
  reject: '驳回',
  rollback: '回退',
  start: '开始',
  complete: '完成',
} as const

// ==================== 阶段显示名称 ====================

export const STAGE_DISPLAY_NAMES: Record<WorkflowStage, string> = {
  created: '已创建',
  product_discussion: '产品研讨',
  ui_design: 'UI设计',
  ui_confirmation: 'UI确认',
  frontend_development: '前端开发',
  backend_development: '后端开发',
  parallel_development: '并行开发',
  code_review: '代码审查',
  ops_deployment: '运维部署',
  testing: '测试',
  product_acceptance: '产品验收',
  done: '已完成',
}

// ==================== 轨道显示名称 ====================

export const TRACK_DISPLAY_NAMES: Record<TrackType, string> = {
  frontend: '前端',
  backend: '后端',
}

export const TRACK_STATUS_DISPLAY_NAMES: Record<TrackStatus, string> = {
  pending: '等待开始',
  in_progress: '开发中',
  in_review: '代码审查中',
  approved: '已通过',
  skipped: '已跳过',
}

// ==================== 时间状态 ====================

export type TimeStatus = 'on_track' | 'approaching_deadline' | 'overdue'

export const TIME_STATUS_LABELS: Record<TimeStatus, string> = {
  on_track: '进度正常',
  approaching_deadline: '临近截止',
  overdue: '已逾期',
}

// ==================== 状态颜色配置 ====================

export const STAGE_STATUS_COLORS = {
  completed: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-400',
    border: 'border-green-300 dark:border-green-700',
  },
  current: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-400',
    border: 'border-blue-300 dark:border-blue-700',
  },
  approaching: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-700 dark:text-yellow-400',
    border: 'border-yellow-300 dark:border-yellow-700',
  },
  overdue: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    border: 'border-red-300 dark:border-red-700',
  },
  pending: {
    bg: 'bg-muted',
    text: 'text-muted-foreground',
    border: 'border-muted',
  },
} as const

export const TRACK_STATUS_COLORS = {
  pending: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-600 dark:text-gray-400',
  },
  in_progress: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
  },
  in_review: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-600 dark:text-orange-400',
  },
  approved: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
  },
  skipped: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-500 dark:text-gray-500',
  },
} as const

// ==================== 轨道边框颜色 ====================

export const TRACK_BORDER_COLORS = {
  frontend: 'border-green-200 dark:border-green-800',
  backend: 'border-blue-200 dark:border-blue-800',
} as const

// ==================== 帮助函数 ====================

export function getStageDisplayName(stage: WorkflowStage): string {
  return STAGE_DISPLAY_NAMES[stage] ?? stage
}

export function getTrackDisplayName(track: TrackType): string {
  return TRACK_DISPLAY_NAMES[track] ?? track
}

export function getTrackStatusDisplayName(status: TrackStatus): string {
  return TRACK_STATUS_DISPLAY_NAMES[status] ?? status
}
